import * as React from 'react';

import { useQuery } from '@apollo/client';

import type { CollectionType } from 'bundles/enterprise-collections/components/CollectionItem';
import CurriculumCollectionsQuery from 'bundles/enterprise-legacy-learner-home/components/single-program/queries/CurriculumCollections.graphql';
import type {
  CurriculumCollectionsQuery as CurriculumCollectionsQueryData,
  CurriculumCollectionsQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/components/single-program/queries/__generated__/CurriculumCollections';
import type { Product as CurriculumProduct } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import EnterpriseCuratedCollectionsContext from 'bundles/page-config-common/contexts/enterprise/EnterpriseCuratedCollectionsContext';
import useCourseEntitiesAsCurriculumCourses from 'bundles/page-config-common/hooks/useCourseEntitiesAsCurriculumCourses';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { CURATED_COLLECTION_MAX_LIMIT, ENTERPRISE_COLLECTION_TYPES } from 'bundles/unified-home-common/constants';

const EnterpriseCuratedCollectionsProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const program = useProgram();
  const features = useEnterpriseProductFeatures();
  const programId = program?.id;
  const { data, loading } = useQuery<CurriculumCollectionsQueryData, CurriculumCollectionsQueryVariables>(
    CurriculumCollectionsQuery,
    {
      variables: {
        programId: programId || '',
        start: '0',
        // Depending on if we are showing browse collections, we may be displaying fewer collections. Fetch the maximum we'll show.
        // EnterpriseCuratedModuleRenderer decides whether we actually render all we fetched.
        limit: CURATED_COLLECTION_MAX_LIMIT,
        enableCurriculumBuilder: Boolean(features?.allowOrgForCurriculumBuilder),
        allowOrgForSpecializationConfiguration: Boolean(features?.allowOrgForSpecializationConfiguration),
      },
      skip: !programId || !features,
      context: { clientName: 'gatewayGql' },
      ssr: false,
      errorPolicy: 'all' as const,
    }
  );
  const allCourseIds =
    data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.elements?.reduce(
      (acc, collection) => {
        if (collection?.courseIds?.length) {
          acc.push(...(collection.courseIds.slice(0, 24) as string[]));
        }

        return acc;
      },
      [] as string[]
    );
  const [curriculumCourseMap, curriculumCourseMapLoading] = useCourseEntitiesAsCurriculumCourses(allCourseIds);

  if (!programId || !features || loading || curriculumCourseMapLoading) {
    return (
      <EnterpriseCuratedCollectionsContext.Provider
        value={{ curatedCollectionsLoading: true, curatedCollections: undefined }}
      >
        {children}
      </EnterpriseCuratedCollectionsContext.Provider>
    );
  }

  const curatedCollectionsData =
    data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.elements;

  const curatedCollections = curatedCollectionsData?.length
    ? curatedCollectionsData.map(
        (collection) =>
          ({
            ...collection,
            courses: {
              elements: curriculumCourseMap
                ? (collection?.courseIds?.map((id) => {
                    if (curriculumCourseMap[id as string]) {
                      return curriculumCourseMap[id as string];
                    } else {
                      return null;
                    }
                  }) as Array<CurriculumProduct | null> | null)
                : [],
            },
            enterpriseProductConfigurations: collection?.programProductMetadata?.elements,
            // This is crticial so that we are able to render the proper collections in the config rendering logic.
            type: ENTERPRISE_COLLECTION_TYPES.CURATED,
            entityTitleComponent: 'h4',
          } as CollectionType)
      )
    : undefined;

  const totalCuratedCollections =
    data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.paging.total || 0;

  return (
    <EnterpriseCuratedCollectionsContext.Provider
      value={{ curatedCollectionsLoading: false, curatedCollections, totalCuratedCollections }}
    >
      {children}
    </EnterpriseCuratedCollectionsContext.Provider>
  );
};

export default EnterpriseCuratedCollectionsProvider;
